import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  BadgeDollarSign,
  BarChart3,
  CreditCard,
  Loader2,
  Megaphone,
  MousePointerClick,
  ShoppingBag,
  Target,
  type LucideIcon,
} from "lucide-react";
import {
  financeService,
  getFunnelFinanceDetail,
  type FinanceMetaAdPerformance,
  type FinanceMetaCampaignPerformance,
} from "../../services/financeService";
import type { FunnelDetailData, Period } from "./types";
import { formatCurrency, formatNumber } from "../dashboard/company/utils";

type FunnelMarketingTabProps = {
  data: FunnelDetailData;
  period: Period;
};

type MarketingMetrics = {
  reach: number;
  spend: number;
  impressions: number;
  clicks: number;
  checkoutViews: number;
  metaPurchases: number;
  purchaseValue: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  clickToCheckout: number;
  metaCpa: number;
  metaRoas: number;
};

type StageInsight = {
  id: "reach" | "impressions" | "clicks" | "checkout" | "purchase";
  icon: LucideIcon;
  title: string;
  metric: string;
  reference: string;
  reading: string;
  action: string;
  tone: "good" | "warning" | "neutral";
};

const BENCHMARKS = {
  ctr: 1.5,
  clickToCheckout: 8,
  cpm: 45000,
};

const formatPercent = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(Number.isFinite(value) ? value : 0)}%`;

const formatMultiplier = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(Number.isFinite(value) ? value : 0)}x`;

const pluralizeCampaigns = (count: number) =>
  `${formatNumber(count)} ${count === 1 ? "campaña conectada" : "campañas conectadas"}`;

const pluralizePurchases = (count: number) =>
  `${formatNumber(count)} ${count === 1 ? "compra" : "compras"}`;

export function FunnelMarketingTab({ data, period }: FunnelMarketingTabProps) {
  const [campaigns, setCampaigns] = useState<FinanceMetaCampaignPerformance[]>([]);
  const [ads, setAds] = useState<FinanceMetaAdPerformance[]>([]);
  const [previousMetrics, setPreviousMetrics] = useState<MarketingMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const previousPeriod = createPreviousPeriod(period);

    Promise.all([
      financeService.getFinanceCampaignPerformance(period),
      financeService.getFinanceAdsPerformance(period),
      previousPeriod
        ? Promise.all([
            financeService.getFinanceCampaignPerformance(previousPeriod),
            getFunnelFinanceDetail(data.funnelId, previousPeriod),
            financeService.getFinanceAdsPerformance(previousPeriod),
          ])
        : Promise.resolve(null),
    ])
      .then(([campaignRows, adRows, previousRows]) => {
        if (!mounted) return;

        const matchedCampaigns = getMatchedCampaigns(campaignRows, data.funnelId);
        const matchedCampaignIds = new Set(matchedCampaigns.map((campaign) => campaign.id));
        const matchedAds = adRows.filter((ad) => matchedCampaignIds.has(ad.campaign_id));
        setCampaigns(matchedCampaigns);
        setAds(matchedAds);

        if (previousRows) {
          const [previousCampaignRows, previousDetail, previousAdRows] = previousRows;
          const previousMatchedCampaigns = getMatchedCampaigns(previousCampaignRows, data.funnelId);
          const previousMatchedCampaignIds = new Set(previousMatchedCampaigns.map((campaign) => campaign.id));
          const previousMatchedAds = previousAdRows.filter((ad) => previousMatchedCampaignIds.has(ad.campaign_id));
          setPreviousMetrics(
            buildMetrics(
              previousMatchedCampaigns,
              previousDetail.stages.checkoutViews,
              previousMatchedAds
            )
          );
        } else {
          setPreviousMetrics(null);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setCampaigns([]);
        setAds([]);
        setPreviousMetrics(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [data.funnelId, period]);

  const metrics = useMemo(
    () => buildMetrics(campaigns, data.stages.checkoutViews, ads),
    [ads, campaigns, data.stages.checkoutViews]
  );

  const insights = useMemo(
    () => buildStageInsights(metrics, previousMetrics),
    [metrics, previousMetrics]
  );

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center text-sm font-medium text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Cargando marketing del embudo...
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#2b5ce6]">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Este embudo aún no tiene campañas conectadas</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
              Cuando conectes campañas, Marketing mostrará cómo la pauta baja hacia clics, checkout y compras.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard
          label="Pauta"
          value={formatCurrency(metrics.spend)}
          detail={pluralizeCampaigns(campaigns.length)}
          icon={BarChart3}
        />
        <MetricCard
          label="CPM"
          value={formatCurrency(metrics.cpm)}
          detail="Costo por mil impresiones"
          icon={Megaphone}
        />
        <MetricCard
          label="CPC"
          value={formatCurrency(metrics.cpc)}
          detail="Costo por clic"
          icon={MousePointerClick}
        />
        <MetricCard
          label="CPA"
          value={metrics.metaPurchases > 0 ? formatCurrency(metrics.metaCpa) : "$ 0"}
          detail={metrics.metaPurchases > 0 ? `${formatNumber(metrics.metaPurchases)} compras` : "sin compras"}
          icon={BadgeDollarSign}
        />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Funnel de marketing del embudo</h2>
            <p className="mt-1 text-sm text-gray-500">
              Mide cómo la atención se convierte en intención y compra.
            </p>
          </div>
          <span className="w-fit rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-bold text-[#2b5ce6]">
            {pluralizeCampaigns(campaigns.length)}
          </span>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2 lg:items-start">
          <div>
            <MarketingFunnel metrics={metrics} />
          </div>
          <div>
            <StageInsights insights={insights} />
          </div>
        </div>
      </section>

      <div className="space-y-4">
        <CampaignsTable campaigns={campaigns} />
        <AdsTable ads={ads} />
      </div>
    </div>
  );
}

function MarketingFunnel({ metrics }: { metrics: MarketingMetrics }) {
  const stages = [
    {
      id: "impressions",
      title: "Impresiones",
      label: "Impactos totales",
      value: formatNumber(metrics.impressions),
      support: `CPM ${formatCurrency(metrics.cpm)}`,
      width: 100,
      color: "#2563eb",
    },
    {
      id: "reach",
      title: "Alcance",
      label: "Personas únicas",
      value: formatNumber(metrics.reach),
      support: `${formatMultiplier(metrics.frequency)} vistas/persona`,
      width: 90,
      color: "#2f6df6",
    },
    {
      id: "clicks",
      title: "Clics",
      label: "Entrada al embudo",
      value: formatNumber(metrics.clicks),
      support: `CTR ${formatPercent(metrics.ctr)}`,
      width: 78,
      color: "#20a697",
    },
    {
      id: "checkout",
      title: "Checkouts",
      label: "Checkouts",
      value: formatNumber(metrics.checkoutViews),
      support: `${formatPercent(metrics.clickToCheckout)} a checkout`,
      width: 66,
      color: "#25324d",
    },
    {
      id: "purchase",
      title: "Compras",
      label: "Compras registradas",
      value: formatNumber(metrics.metaPurchases),
      support: metrics.metaPurchases > 0 && metrics.metaRoas === 0 ? "Recaudo pendiente" : `ROAS ${formatMultiplier(metrics.metaRoas)}`,
      width: 56,
      color: "#111827",
    },
  ];

  return (
    <div className="w-full rounded-lg bg-slate-50/60 px-3 py-4 sm:px-5">
      <div className="flex flex-col gap-3">
        {stages.map((stage) => (
          <div key={stage.id} className="mx-auto" style={{ width: `${stage.width}%`, minWidth: "min(100%, 280px)" }}>
            <div className="relative h-[72px] text-white">
              <div
                className="absolute inset-0 shadow-sm"
                style={{
                  backgroundColor: stage.color,
                  clipPath: "polygon(0 0, 100% 0, 92% 100%, 8% 100%)",
                }}
              />
              <div
                className="relative flex h-[72px] items-center justify-between gap-3 py-3"
                style={{ paddingLeft: "9%", paddingRight: "9%" }}
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-normal text-white/70">{stage.title}</p>
                  <p className="mt-1 text-sm font-semibold leading-tight">{stage.label}</p>
                </div>
                <div className="w-[124px] shrink-0 text-right">
                  <p className="whitespace-nowrap text-xl font-bold tracking-normal sm:text-2xl">{stage.value}</p>
                  <p className="mt-0.5 text-[11px] font-semibold leading-tight text-white/70">{stage.support}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageInsights({ insights }: { insights: StageInsight[] }) {
  return (
    <aside className="grid gap-3">
      {insights.map((insight) => (
        <StageInsightRow key={insight.id} insight={insight} />
      ))}
    </aside>
  );
}

function StageInsightRow({ insight }: { insight: StageInsight }) {
  const toneClass =
    insight.tone === "good"
      ? "border-l-emerald-400 text-emerald-900"
      : insight.tone === "warning"
      ? "border-l-amber-400 text-amber-900"
      : "border-l-slate-300 text-slate-800";
  const Icon = insight.icon;

  return (
    <div className={`flex h-[72px] items-center rounded-md border border-l-4 border-gray-100 bg-white px-3 py-2 ${toneClass}`}>
      <div className="flex w-full items-center gap-2.5">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-50 text-current">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
            <p className="text-sm font-bold">{insight.title}</p>
            <p className="text-xs font-semibold opacity-70">{insight.metric}</p>
          </div>
          <p className="mt-1 text-xs font-medium leading-5 opacity-80">{insight.reading}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase text-gray-400">{label}</p>
          <p className="mt-1 truncate text-lg font-bold tracking-normal text-gray-900">{value}</p>
          <p className="mt-1 text-xs font-medium text-gray-400">{detail}</p>
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-50 text-[#2b5ce6]">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

function CampaignsTable({ campaigns }: { campaigns: FinanceMetaCampaignPerformance[] }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">Campañas conectadas</h3>
        <p className="mt-1 text-sm text-gray-500">Dónde se concentra la inversión y qué devuelve.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <TableHead>Campaña</TableHead>
              <TableHead align="right">Pauta</TableHead>
              <TableHead align="right">Compras</TableHead>
              <TableHead align="right">CPA</TableHead>
              <TableHead align="right">ROAS</TableHead>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {[...campaigns].sort((a, b) => b.spend - a.spend).map((campaign) => {
              const cpa = campaign.purchases > 0 ? campaign.spend / campaign.purchases : 0;

              return (
                <tr key={campaign.id}>
                  <td className="max-w-[360px] px-5 py-4">
                    <p className="truncate text-sm font-semibold text-gray-900">{campaign.name}</p>
                    <p className="mt-1 text-xs text-gray-400">{campaign.effective_status ?? "Sin estado"}</p>
                  </td>
                  <TableCell>{formatCurrency(campaign.spend)}</TableCell>
                  <TableCell>{formatNumber(campaign.purchases)}</TableCell>
                  <TableCell>{campaign.purchases > 0 ? formatCurrency(cpa) : "$ 0"}</TableCell>
                  <TableCell>{formatMultiplier(campaign.roas)}</TableCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdsTable({ ads }: { ads: FinanceMetaAdPerformance[] }) {
  const topAds = [...ads].sort((a, b) => b.spend - a.spend).slice(0, 8);

  if (topAds.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">Anuncios principales</h3>
        <p className="mt-1 text-sm text-gray-500">Úsalos para detectar qué creatividad mueve el embudo.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <TableHead>Anuncio</TableHead>
              <TableHead align="right">Pauta</TableHead>
              <TableHead align="right">CTR</TableHead>
              <TableHead align="right">CPC</TableHead>
              <TableHead align="right">Frecuencia</TableHead>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {topAds.map((ad) => (
              <tr key={ad.id}>
                <td className="max-w-[360px] px-5 py-4">
                  <p className="truncate text-sm font-semibold text-gray-900">{ad.name}</p>
                  <p className="mt-1 truncate text-xs text-gray-400">{ad.campaign_name ?? "Sin campaña"}</p>
                </td>
                <TableCell>{formatCurrency(ad.spend)}</TableCell>
                <TableCell>{formatPercent(ad.ctr)}</TableCell>
                <TableCell>{formatCurrency(ad.cpc)}</TableCell>
                <TableCell>{formatMultiplier(ad.frequency)}</TableCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TableHead({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`whitespace-nowrap px-5 py-3 text-xs font-bold uppercase tracking-normal text-gray-400 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function TableCell({ children }: { children: ReactNode }) {
  return (
    <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-gray-700">
      {children}
    </td>
  );
}

function buildMetrics(
  campaignRows: FinanceMetaCampaignPerformance[],
  checkoutViews: number,
  adRows: FinanceMetaAdPerformance[] = []
): MarketingMetrics {
  const reach = adRows.reduce((sum, row) => sum + Number(row.reach ?? 0), 0);
  const spend = campaignRows.reduce((sum, row) => sum + Number(row.spend ?? 0), 0);
  const impressions = campaignRows.reduce((sum, row) => sum + Number(row.impressions ?? 0), 0);
  const clicks = campaignRows.reduce((sum, row) => sum + Number(row.clicks ?? 0), 0);
  const metaPurchases = campaignRows.reduce((sum, row) => sum + Number(row.purchases ?? 0), 0);
  const purchaseValue = campaignRows.reduce((sum, row) => sum + Number(row.purchase_value ?? 0), 0);

  return {
    reach,
    spend,
    impressions,
    clicks,
    checkoutViews,
    metaPurchases,
    purchaseValue,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpc: clicks > 0 ? spend / clicks : 0,
    cpm: impressions > 0 ? (spend / impressions) * 1000 : 0,
    frequency: reach > 0 ? impressions / reach : 0,
    clickToCheckout: clicks > 0 ? (checkoutViews / clicks) * 100 : 0,
    metaCpa: metaPurchases > 0 ? spend / metaPurchases : 0,
    metaRoas: spend > 0 ? purchaseValue / spend : 0,
  };
}

function buildStageInsights(
  metrics: MarketingMetrics,
  previousMetrics: MarketingMetrics | null
): StageInsight[] {
  const cpmReference = previousMetrics?.cpm && previousMetrics.cpm > 0 ? previousMetrics.cpm : BENCHMARKS.cpm;
  const ctrReference = previousMetrics?.ctr && previousMetrics.ctr > 0 ? previousMetrics.ctr : BENCHMARKS.ctr;
  const checkoutReference =
    previousMetrics?.clickToCheckout && previousMetrics.clickToCheckout > 0
      ? previousMetrics.clickToCheckout
      : BENCHMARKS.clickToCheckout;

  return [
    buildImpressionsInsight(metrics, cpmReference, Boolean(previousMetrics?.cpm)),
    buildReachInsight(metrics),
    buildClicksInsight(metrics, ctrReference, Boolean(previousMetrics?.ctr)),
    buildCheckoutInsight(metrics, checkoutReference, Boolean(previousMetrics?.clickToCheckout)),
    buildPurchaseInsight(metrics),
  ];
}

function buildReachInsight(metrics: MarketingMetrics): StageInsight {
  if (metrics.reach === 0) {
    return {
      id: "reach",
      icon: Megaphone,
      title: "Alcance",
      metric: "sin alcance",
      reference: "Aún no hay entrega",
      reading: "No hay personas alcanzadas en el periodo.",
      action: "Revisa estado de campaña y presupuesto.",
      tone: "warning",
    };
  }

  const isHealthy = metrics.frequency <= 3.5;
  return {
    id: "reach",
    icon: Megaphone,
    title: "Alcance",
    metric: `${formatNumber(metrics.reach)} personas`,
    reference: `Frecuencia ${formatMultiplier(metrics.frequency)}`,
    reading: isHealthy
      ? `${formatMultiplier(metrics.frequency)} vistas por persona; mide repetición sobre audiencia única.`
      : `Audiencia repetida: ${formatMultiplier(metrics.frequency)} vistas por persona.`,
    action: isHealthy ? "Evalúa si ese alcance está trayendo clics." : "Amplía audiencia o refresca creatividad.",
    tone: isHealthy ? "good" : "warning",
  };
}

function buildImpressionsInsight(
  metrics: MarketingMetrics,
  reference: number,
  usesHistory: boolean
): StageInsight {
  if (metrics.impressions === 0) {
    return {
      id: "impressions",
      icon: Megaphone,
      title: "Impresiones",
      metric: "sin impresiones",
      reference: "Aún no hay volumen",
      reading: "No hay impresiones entregadas en el periodo.",
      action: "Revisa estado, presupuesto y cuenta publicitaria conectada.",
      tone: "warning",
    };
  }

  const isEfficient = metrics.cpm <= reference * 1.15;
  return {
    id: "impressions",
    icon: Megaphone,
    title: "Impresiones",
    metric: `${formatNumber(metrics.impressions)} impresiones`,
    reference: `${usesHistory ? "Periodo anterior" : "Benchmark"} ${formatCurrency(reference)}`,
    reading: isEfficient
      ? `CPM ${formatCurrency(metrics.cpm)}; costo por cada mil veces que el anuncio se mostró.`
      : `CPM ${formatCurrency(metrics.cpm)}; costo por encima de la referencia.`,
    action: isEfficient ? "Mantén volumen y evalúa clics." : "Ajusta audiencia, creatividad o presupuesto.",
    tone: isEfficient ? "good" : "warning",
  };
}

function buildClicksInsight(
  metrics: MarketingMetrics,
  reference: number,
  usesHistory: boolean
): StageInsight {
  if (metrics.clicks === 0) {
    return {
      id: "clicks",
      icon: MousePointerClick,
      title: "Clics",
      metric: "sin clics",
      reference: "Sin interacción visible",
      reading: "Hay impresiones, pero no clics hacia el embudo.",
      action: "Revisa gancho, promesa y primer visual.",
      tone: "warning",
    };
  }

  const isHealthy = metrics.ctr >= reference;
  return {
    id: "clicks",
    icon: MousePointerClick,
    title: "Clics",
    metric: `${formatNumber(metrics.clicks)} clics`,
    reference: `${usesHistory ? "Periodo anterior" : "Benchmark"} ${formatPercent(reference)}`,
    reading: isHealthy
      ? `CTR ${formatPercent(metrics.ctr)}; buen paso de impresión a clic.`
      : `CTR ${formatPercent(metrics.ctr)}; falta más intención desde los anuncios.`,
    action: isHealthy ? "Mira CPC por anuncio para escalar piezas." : "Prueba ángulos y creatividades nuevas.",
    tone: isHealthy ? "good" : "warning",
  };
}

function buildCheckoutInsight(
  metrics: MarketingMetrics,
  reference: number,
  usesHistory: boolean
): StageInsight {
  if (metrics.checkoutViews === 0 && metrics.metaPurchases > 0) {
    return {
      id: "checkout",
      icon: Target,
      title: "Checkouts",
      metric: "0 checkouts",
      reference: `${formatNumber(metrics.metaPurchases)} compras`,
      reading: `Hay ${pluralizePurchases(metrics.metaPurchases)}, pero este embudo no registra checkout.`,
      action: "Revisa el match del embudo antes de tomar esta señal como definitiva.",
      tone: "warning",
    };
  }

  if (metrics.checkoutViews === 0) {
    return {
      id: "checkout",
      icon: Target,
      title: "Checkouts",
      metric: "0 checkouts",
      reference: "Sin llegada al checkout",
      reading: "Hay clics, pero no llegada al checkout en este embudo.",
      action: "Revisa página, continuidad del mensaje y evento de checkout.",
      tone: "warning",
    };
  }

  const isHealthy = metrics.clickToCheckout >= reference;
  return {
    id: "checkout",
    icon: Target,
    title: "Checkouts",
    metric: `${formatNumber(metrics.checkoutViews)} checkouts`,
    reference: `${usesHistory ? "Periodo anterior" : "Benchmark"} ${formatPercent(reference)}`,
    reading: isHealthy
      ? `${formatPercent(metrics.clickToCheckout)} de clics llegan a checkout.`
      : `Solo ${formatPercent(metrics.clickToCheckout)} de clics llegan a checkout.`,
    action: isHealthy ? "Pasa a Finanzas para validar CPA rentable." : "Alinea anuncio, página, precio y oferta.",
    tone: isHealthy ? "good" : "warning",
  };
}

function buildPurchaseInsight(metrics: MarketingMetrics): StageInsight {
  if (metrics.metaPurchases === 0) {
    return {
      id: "purchase",
      icon: ShoppingBag,
      title: "Compras",
      metric: "0 compras",
      reference: "ROAS 0x",
      reading: "No hay compras registradas en el periodo.",
      action: "Con más datos, esta etapa indicará si la campaña devuelve inversión.",
      tone: "neutral",
    };
  }

  return {
    id: "purchase",
    icon: CreditCard,
    title: "Compras",
    metric: pluralizePurchases(metrics.metaPurchases),
    reference: `ROAS ${formatMultiplier(metrics.metaRoas)}`,
    reading:
      metrics.metaRoas > 0
        ? `CPA ${formatCurrency(metrics.metaCpa)} y ROAS ${formatMultiplier(metrics.metaRoas)}.`
        : `CPA ${formatCurrency(metrics.metaCpa)}; falta recaudo registrado para calcular retorno.`,
    action: "Valida si el CPA cabe dentro del margen del embudo.",
    tone: "good",
  };
}

function createPreviousPeriod(period: Period): Period | null {
  if (period.id === "all-time") return null;

  const previousTo = new Date(period.from);
  previousTo.setDate(previousTo.getDate() - 1);

  const days = Math.max(1, period.days || 1);
  const previousFrom = new Date(previousTo);
  previousFrom.setDate(previousFrom.getDate() - days + 1);

  return {
    id: "custom",
    label: "Periodo anterior",
    from: previousFrom,
    to: previousTo,
    days,
  };
}

function getMatchedCampaigns(
  campaigns: FinanceMetaCampaignPerformance[],
  funnelId: string
) {
  return campaigns.filter((campaign) => campaign.matched_funnel_ids?.includes(funnelId));
}
